/**
 * ProjectRiskSummary — view-only summary page for a single project.
 *
 * Visible to Admin / HR / Process Compliance Manager / Process Compliance
 * Officer, the assigned PM, and project Team Members (Employee).
 *
 * Layout:
 *   - KPI cards (Total / High / Open / Mitigated / Overdue)
 *   - Recharts BarChart of area-wise average exposure
 *   - Area wise Weight PieChart, Area wise % Exposure BarChart,
 *     Top 15 Risks ScatterChart
 *   - MUI DataGrid of the top risks
 *   - 10x10 probability vs impact heatmap
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  Button,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TimelineIcon from "@mui/icons-material/Timeline";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import PageTitle from "../../../components/Page Component/PageTitle";
import { fetchData } from "../../../store/actions";
import { useCustomToast } from "../../../components/Methods/SnackBar";
import CustomNoRowsOverlay from "../../../components/Data/NoRowsOverlayComponent";
import { projectRiskSummaryUrl } from "../../../Service/Apis";
import {
  ProjectRiskSummary as SummaryDto,
} from "./Types/RiskManagementTypes";
import RiskScoreBadge, { levelColor } from "./components/RiskScoreBadge";
import RiskStatusChip from "./components/RiskStatusChip";
import RiskHeatmap from "./components/RiskHeatmap";
import { normalizeProjectSummary } from "./components/normalizers";

const AREA_COLORS = [
  "#1976d2", "#388e3c", "#ed6c02", "#9c27b0", "#00838f",
  "#c62828", "#4527a0", "#ef6c00", "#2e7d32",
];

const ProjectRiskSummary: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const showToast = useCustomToast();

  const [summary, setSummary] = useState<SummaryDto | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSummary = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    dispatch(fetchData(projectRiskSummaryUrl(projectId)))
      .then((res: any) => {
        // The dashboard endpoint exposes a nested shape (`totals.high`,
        // `area_averages[].area`) and a 10x10 heatmap grid. The normalizer
        // flattens that into the structure this page renders against
        // (`total_risks`, `area_averages[].area_name`, heatmap cells, etc.)
        // and also normalizes the embedded worksheet object.
        setSummary(normalizeProjectSummary(res) as SummaryDto);
      })
      .catch(() => showToast("Failed to load risk summary.", "error", false))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, projectId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 12, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!summary) {
    return (
      <Container maxWidth="md" sx={{ mt: 12 }}>
        <Alert severity="error">No risk summary available for this project.</Alert>
      </Container>
    );
  }

  const ws = summary.worksheet;
  const kpis = [
    { label: "Total Risks", value: summary.total_risks, color: "#1976d2" },
    { label: "HIGH Risks", value: summary.high_count, color: "#c62828" },
    { label: "Open", value: summary.open_count, color: "#ed6c02" },
    { label: "Mitigated", value: summary.mitigated_count, color: "#2e7d32" },
    { label: "Overdue", value: summary.overdue_count, color: "#9c27b0" },
  ];

  const areaBars = summary.area_averages.map((a) => ({
    area: a.area_name || "—",
    Exposure: Math.round(Number(a.avg_exposure ?? 0)),
    High: a.high_count,
    Medium: a.medium_count,
    Low: a.low_count,
  }));

  const totalRiskCount = summary.area_averages.reduce(
    (sum, a) => sum + (a.risk_count ?? 0),
    0,
  );

  const areaWeightData = summary.area_averages.map((a) => ({
    name: a.area_name || "—",
    value:
      totalRiskCount > 0
        ? Math.round(((a.risk_count ?? 0) / totalRiskCount) * 10000) / 100
        : 0,
    count: a.risk_count ?? 0,
  }));

  const exposurePctBars = summary.area_averages.map((a) => ({
    area: a.area_name || "—",
    "% Exposure": Math.round(Number(a.avg_exposure ?? 0) * 100) / 100,
  }));

  const topRisksScatter = summary.top_risks.slice(0, 15).map((r, i) => ({
    rank: i + 1,
    exposure: r.exposure,
    area_name: r.area_name,
    contributor_name: r.contributor_name,
  }));

  const topColumns: GridColDef[] = [
    { field: "area_name", headerName: "Area", width: 140 },
    { field: "contributor_name", headerName: "Contributor", width: 160 },
    {
      field: "level",
      headerName: "Level / Exposure",
      width: 160,
      renderCell: (p: GridRenderCellParams) => (
        <RiskScoreBadge level={p.value as any} exposure={p.row.exposure} />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (p: GridRenderCellParams) => (
        <RiskStatusChip status={p.value as string} />
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 64,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (p) => (
        <Tooltip title="View risk trend">
          <IconButton
            size="small"
            onClick={() =>
              navigate(`/pms/risk-management/risk/${p.row.risk_id}/history`)
            }
          >
            <TimelineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const topRows = summary.top_risks.map((r) => ({ id: r.risk_id, ...r }));

  return (
    <Container maxWidth="xl" sx={{ mt: 10, mb: 6 }}>
      <PageTitle>Project Risk Summary</PageTitle>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="overline" color="text.secondary">
                Project
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {ws.project_name ?? ws.project}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                PM: <b>{ws.current_pm_name ?? "Unassigned"}</b> • Cycle:{" "}
                {ws.current_cycle_name ?? "—"} • Status:{" "}
                <Chip
                  size="small"
                  label={
                    ws.current_cycle_status === "submitted"
                      ? "OPEN ✓ submitted"
                      : (ws.current_cycle_status ?? "—").toUpperCase()
                  }
                />
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="overline" color="text.secondary">
                Overall Risk
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <RiskScoreBadge
                  level={ws.current_overall_level}
                  exposure={ws.current_overall_exposure ?? undefined}
                  size="medium"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={3} sx={{ textAlign: { md: "right" } }}>
              <Button
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                onClick={() =>
                  navigate(`/pms/risk-management/workspace/${ws.id}`)
                }
              >
                Open worksheet
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {kpis.map((k) => (
          <Grid key={k.label} item xs={6} sm={4} md={2.4}>
            <Card elevation={2}>
              <CardContent sx={{ textAlign: "center", py: 1.5 }}>
                <Typography variant="h4" fontWeight="bold" color={k.color}>
                  {k.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {k.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, height: 360 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Average exposure by risk area
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={areaBars}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="area" interval={0} angle={-25} textAnchor="end" height={70} />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="High" stackId="counts" fill={levelColor("HIGH")} />
                <Bar dataKey="Medium" stackId="counts" fill={levelColor("MEDIUM")} />
                <Bar dataKey="Low" stackId="counts" fill={levelColor("LOW")} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 400 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Area wise Weight
            </Typography>
            <ResponsiveContainer width="100%" height={340}>
              <PieChart>
                <Pie
                  data={areaWeightData}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={110}
                  label={({ value }) => `${value}%`}
                >
                  {areaWeightData.map((_d, i) => (
                    <Cell
                      key={i}
                      fill={AREA_COLORS[i % AREA_COLORS.length]}
                    />
                  ))}
                </Pie>
                <RechartsTooltip
                  formatter={(value: number, _name: string, props: any) => [
                    `${value}% (${props.payload.count} risks)`,
                    props.payload.name,
                  ]}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 400 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Area wise % Exposure
            </Typography>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={exposurePctBars}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="area"
                  interval={0}
                  angle={-35}
                  textAnchor="end"
                  height={80}
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                />
                <RechartsTooltip
                  formatter={(value: number) => [`${value}%`, "Exposure"]}
                />
                <Bar dataKey="% Exposure" fill="#1976d2">
                  {exposurePctBars.map((_d, i) => (
                    <Cell
                      key={i}
                      fill={AREA_COLORS[i % AREA_COLORS.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, overflow: "auto" }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Probability × Impact heatmap
            </Typography>
            <RiskHeatmap cells={summary.heatmap} cellSize={26} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Top 15 Risks
            </Typography>
            <ResponsiveContainer width="100%" height={340}>
              <ScatterChart
                margin={{ top: 10, right: 20, bottom: 30, left: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="rank"
                  type="number"
                  domain={[0, 16]}
                  tickCount={16}
                  tick={{ fontSize: 11 }}
                >
                  <Label
                    value="Risk in Rank Order"
                    position="bottom"
                    offset={10}
                    style={{ fontSize: 12 }}
                  />
                </XAxis>
                <YAxis
                  dataKey="exposure"
                  type="number"
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                />
                <RechartsTooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <Paper sx={{ p: 1, fontSize: 12 }} elevation={3}>
                        <Typography variant="body2" display="block">
                          {d.area_name}: {d.contributor_name}
                        </Typography>
                        <Typography variant="caption" display="block" color="error">
                          Exposure: {d.exposure}%
                        </Typography>
                      </Paper>
                    );
                  }}
                />
                <Scatter
                  data={topRisksScatter}
                  fill="#c62828"
                  shape="circle"
                />
              </ScatterChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ mt: 2, p: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Top risks
        </Typography>
        <Box sx={{ height: 420, width: "100%" }}>
          <DataGrid
            rows={topRows}
            columns={topColumns}
            disableRowSelectionOnClick
            slots={{ noRowsOverlay: CustomNoRowsOverlay }}
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } },
            }}
            pageSizeOptions={[10, 25, 50]}
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default ProjectRiskSummary;
